import type { FC } from "react";

import { useHomeRole } from "components/materials/home/use-home-role";
import type { SectionType } from "components/server-types";
import type { LargeSizeType } from "mds/global-types";
import {
  BookSectionIcon,
  BookSectionLinkedIcon,
  BookSectionTemplateIcon,
  EyeNotVisibleIcon,
} from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useIsArchived } from "utils/hooks/use-is-archived";
import { isTemplateSection, isUnlinkedSection } from "utils/publishing";
import { useViewLevel } from "utils/urls";
import { isCCMEnabled } from "utils/waffle-flags";

export type SectionIconProps = {
  section: SectionType;
  size?: LargeSizeType;
};

export const SectionIcon: FC<SectionIconProps> = ({ section, size = "m" }) => {
  const { isArchived } = useIsArchived();
  const ccmEnabled = isCCMEnabled();
  const viewLevel = useViewLevel();
  const { currentRole } = useHomeRole();
  const { canParticipateInSection } = useCapabilities();
  // TODO: eventually we should use capabilities for home role
  const canParticipate = viewLevel.home ? currentRole === "student" : canParticipateInSection;

  if (isArchived(section)) return <EyeNotVisibleIcon size={size} />;

  if (isUnlinkedSection(section) || canParticipate || !ccmEnabled) {
    return (
      <span className="text-black-tint-40">
        <BookSectionIcon size={size} />
      </span>
    );
  }

  if (isTemplateSection(section))
    return (
      <span className="text-hibiscus-tint-40">
        <BookSectionTemplateIcon size={size} />
      </span>
    );

  return (
    <span className="text-moss-tint-30">
      <BookSectionLinkedIcon size={size} />
    </span>
  );
};
